import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Panel} from '@Components/panel';
import {PublishingPanelHeader} from '@Panels/poster-publishing-options-panel/components/publishing-panel-header/publishing-panel-header';
import {DigitalSignageCompiledSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-compiled-sub-panel/digital-signage-compiled-sub-panel';
import {
  resetPanelState,
  setDisplayType,
  setErrorMessageForPublishing,
  setErrorOnPublishPanel,
  setFreebieProduct,
  setHashedId,
  setPremiumProductForPoster,
  setPremiumProductForPrint,
  setSocialMediaLinks,
  setWebLinksData,
  webpageEmbedFlow,
  webpagePublishFlow,
} from '@Panels/poster-publishing-options-panel/poster-publishing-options-slice';
import useCustomPanelClose from '@Panels/hooks/useCustomPanelClose';
import ErrorPanel from '@Panels/error-panel';
import '../../../../../assets/css/components/_animations.scss';
import {closePanel} from '@Components/panel/panel-reducer';
import {useErrorMessageForPublishing, useSelectionType, useShowError, useSubFlowType} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {PublishingOptionsDefaultList} from '@Panels/poster-publishing-options-panel/components/publishing-options-default-list/publishing-options-default-list';
import {PublishingDigitalSignageList} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list';
import {PublishingAsWebpagePanel} from '@Panels/poster-publishing-options-panel/components/publishing-as-webpage-panel/publishing-as-webpage-panel';
import {PublishingEmbedInWebpagePanel} from '@Panels/poster-publishing-options-panel/components/publishing-embed-in-webpage-panel/publishing-embed-in-webpage-panel';
import {PublishingSocialMediaPanel} from '@Panels/poster-publishing-options-panel/components/publishing-social-media-panel/publishing-social-media-panel';
import {PublishingShareToAppPanel} from '@Panels/poster-publishing-options-panel/components/publishing-share-to-app-panel/publishing-share-to-app-panel';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {PublishingLoaderScreen} from '@Panels/poster-publishing-options-panel/components/publishing-loader-screen/publishing-loader-screen';
import type {AJAXResponseProductDataComplete, PosterPublishingOptionsPanelProps, PosterProductDataForProcessDesign} from './poster-publishing-options-panel.types';
import {DisplayType, PUBLISHING_OPTIONS_PANEL_ID, PRINT_QUALITY_IMAGE_INDEX_FOR_PUBLISHING, SelectionType} from './poster-publishing-options-panel.types';
import styles from './poster-publishing-options-panel.module.scss';
import {useAppDispatch} from '@/hooks';
import {PublishingQRCodePanel} from '@Panels/poster-publishing-options-panel/components/publishing-qr-code-panel/publishing-qr-code-panel';
import {PublishingRedirectConfirmationPanel} from '@Panels/poster-publishing-options-panel/components/publishing-redirect-confirmation-panel/publishing-redirect-confirmation-panel';

export function PosterPublishingOptionsPanel({defaultPanelFlowState = SelectionType.DEFAULT_DISPLAY, panelDetails, ...props}: PosterPublishingOptionsPanelProps): ReactElement<any> {
  const {displayType, onClose} = panelDetails;
  const [isLoading, setIsLoading] = useState(false);
  const [topShadow, setTopShadow] = useState(false);
  const selectionType = useSelectionType();
  const subFlowType = useSubFlowType();
  const errorMessage = useErrorMessageForPublishing();
  const showError = useShowError();
  const dispatch = useAppDispatch();
  const panelContentRef = useRef<HTMLDivElement>(null);

  const shouldShowPanel = (): boolean => {
    return displayType === DisplayType.MODAL;
  };

  const getPosterProductDataIndex = (productData: PosterProductDataForProcessDesign[]): number => {
    return productData.length - 1;
  };

  const isPosterQualityProductAvailable = (response: AJAXResponseProductDataComplete): boolean => {
    return response.productData.length > 1 && !!response.productData[getPosterProductDataIndex(response.productData)];
  };

  const customCloseFunction = (): void => {
    if (onClose) {
      onClose();
    }
    dispatch(closePanel(PUBLISHING_OPTIONS_PANEL_ID));
    dispatch(resetPanelState());
    dispatch(setErrorOnPublishPanel(false));
  };
  const customClose = useCustomPanelClose(PUBLISHING_OPTIONS_PANEL_ID, customCloseFunction);

  const dispatchAllProductDataResponse = (response: AJAXResponseProductDataComplete): void => {
    if (props.idPosterHash) {
      dispatch(setHashedId(props.idPosterHash));
    }

    dispatch(setDisplayType(displayType));
    dispatch(setSocialMediaLinks(response.socialMediaLinks));

    if (response.freebieProduct) {
      response.freebieProduct.id = Number(response.freebieProduct.id);
      dispatch(setFreebieProduct(response.freebieProduct));
    }

    if (response.productData[PRINT_QUALITY_IMAGE_INDEX_FOR_PUBLISHING]) {
      response.productData[PRINT_QUALITY_IMAGE_INDEX_FOR_PUBLISHING].id = Number(response.productData[PRINT_QUALITY_IMAGE_INDEX_FOR_PUBLISHING].id);
      dispatch(setPremiumProductForPrint(response.productData[PRINT_QUALITY_IMAGE_INDEX_FOR_PUBLISHING]));
    }
    if (isPosterQualityProductAvailable(response)) {
      const index = getPosterProductDataIndex(response.productData);
      response.productData[index].id = Number(response.productData[index].id);
      dispatch(setPremiumProductForPoster(response.productData[index]));
    }
    if (response.webLinks) {
      dispatch(setWebLinksData(response.webLinks));
    }
  };

  useEffect(() => {
    if (!panelContentRef.current) {
      return undefined;
    }

    const observer = new MutationObserver(() => {
      const innerContainer = panelContentRef.current?.querySelector('*');
      if (innerContainer && panelContentRef.current) {
        panelContentRef.current.style.height = `${innerContainer.clientHeight}px`;
      }
    });

    const targetNode = panelContentRef.current;
    const config = {attributes: true, childList: true, subtree: true};

    if (targetNode) {
      observer.observe(targetNode, config);
    }
    return (): void => {
      if (targetNode) {
        observer.disconnect();
      }
    };
  }, []);

  const setSelectionTypeStateForPublishingFlow = (): void => {
    if (defaultPanelFlowState !== SelectionType.DEFAULT_DISPLAY) {
      if (defaultPanelFlowState === SelectionType.EMBED_WEBPAGE_DISPLAY) {
        dispatch(webpageEmbedFlow());
      } else if (defaultPanelFlowState === SelectionType.PUBLISH_WEBPAGE_DISPLAY) {
        dispatch(webpagePublishFlow());
      }
    }
  };

  useEffect(() => {
    setIsLoading(true);
    setSelectionTypeStateForPublishingFlow();
    void window.PMW.writeLocal('poster/getPosterAndProductsNew', props)
      .then((response: AJAXResponseProductDataComplete): void => {
        dispatchAllProductDataResponse(response);
      })
      .catch(() => {
        dispatch(setErrorOnPublishPanel(true));
        dispatch(setErrorMessageForPublishing(window.i18next.t('pmwjs_error_message')));
      })
      .finally(() => {
        setIsLoading(false);
      });

    return (): void => {
      dispatch(resetPanelState());
    };
  }, []);

  const getSpacingForDropDown = (): string => {
    switch (selectionType) {
      case SelectionType.PUBLISH_WEBPAGE_DISPLAY:
      case SelectionType.EMBED_WEBPAGE_DISPLAY:
        return 'flex-v-row spacing-p-3';
      case SelectionType.DEFAULT_DISPLAY:
      case SelectionType.DIGITAL_SIGNAGE_DISPLAY:
      case SelectionType.SOCIAL_MEDIA_DISPLAY:
        return '';
      default:
        return '';
    }
  };

  const getPanelDisplayContent = (): ReactElement<any> | undefined => {
    switch (selectionType) {
      case SelectionType.DEFAULT_DISPLAY:
        return <PublishingOptionsDefaultList closePanel={customClose} displayType={displayType} recommendedOptions={props.recommendedOptions}/>;
      case SelectionType.DIGITAL_SIGNAGE_DISPLAY:
        return <PublishingDigitalSignageList displayType={displayType} setTopShadow={setTopShadow} />;
      case SelectionType.PUBLISH_WEBPAGE_DISPLAY:
        return <PublishingAsWebpagePanel displayType={displayType} onTogglePublish={props.onSuccessfulConfirmation}/>;
      case SelectionType.EMBED_WEBPAGE_DISPLAY:
        return <PublishingEmbedInWebpagePanel displayType={displayType} />;
      case SelectionType.SOCIAL_MEDIA_DISPLAY:
        return <PublishingSocialMediaPanel displayType={displayType} />;
      case SelectionType.SHARE_TO_APP:
        return <PublishingShareToAppPanel />;
      case SelectionType.QR_CODE:
        return <PublishingQRCodePanel displayType={displayType} onTogglePublish={props.onSuccessfulConfirmation}/>;
      case SelectionType.CONFIRMATION_DISPLAY:
        return <PublishingRedirectConfirmationPanel onClose={customClose} />;
      default:
        return undefined;
    }
  };

  const getPanelContent = (): ReactElement<any> => {
    return isLoading && selectionType !== SelectionType.DEFAULT_DISPLAY ? (
      <PublishingLoaderScreen customHeight={styles.mainLoaderHeight} loaderSize={LOADER_SIZE.LARGE} />
    ) : (
      <div
        ref={panelContentRef}
        className={`hover-transition-all _full-height ${selectionType === SelectionType.DEFAULT_DISPLAY || selectionType === SelectionType.DIGITAL_SIGNAGE_DISPLAY ? '_overflow-auto' : ''}`}
      >
        {getPanelDisplayContent()}
      </div>
    );
  };
  const getPanel = (): ReactElement<any> => {
    return selectionType === SelectionType.SUB_DISPLAY ? (
      <div ref={panelContentRef} className={`hover-transition-all _full-height ${styles.handleTransition}`}>
        <DigitalSignageCompiledSubPanel onClose={customClose} subFlowType={subFlowType} />
      </div>
    ) : (
      <Panel
        panelId={PUBLISHING_OPTIONS_PANEL_ID}
        panelBodyClassName={`${shouldShowPanel() ? '' : `radius-4 ${getSpacingForDropDown()}`}  shadow-floating-elevation-3 _overflow-auto`}
        panelHeader={<PublishingPanelHeader onClose={customClose} showShadow={topShadow} displayType={displayType} />}
        panelContent={getPanelContent()}
      />
    );
  };

  const showErrorPanel = (): ReactElement<any> => {
    return (
      <ErrorPanel
        panelId={PUBLISHING_OPTIONS_PANEL_ID}
        onClose={customClose}
        log={false}
        showCloseButton={shouldShowPanel()}
        customClass="spacing-p-t-4 content-body"
        title={window.i18next.t('pmwjs_error_title')}
        message={errorMessage !== '' ? errorMessage : window.i18next.t('pmwjs_something_wrong_reload_page')}
      />
    );
  };
  return showError ? showErrorPanel() : getPanel();
}
